import React, {useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import Copy from '../../components/cms/copy'
import Heading from '../../components/cms/heading'
import ModuleContainer from '../../components/cms/module-container'
import ProductAnimated from './product-animated'
import {Box, useSlotRecipe} from '@chakra-ui/react3'
import productAnimatedModuleRecipe from './index.recipe'
import Fade from '../../components/cms/fade'
import {useScroll, useReducedMotion} from 'framer-motion'
import useResizeObserver from '../../../hooks/use-resize-observer'
import useHydration from '../../../contexts/hydration'

const ProductAnimatedModule = React.forwardRef(
    (
        {
            moduleId,
            moduleType,
            pageId,
            anchor,
            heading,
            headingLevel,
            copy,
            animatedProduct,
            containerHeight,
            background,
            desktopDisplay = 'product-below-copy'
        },
        forwardRef
    ) => {
        const isHydrated = useHydration()
        const layout = desktopDisplay
        const {subline, headline1, headline2, variant} = heading
        const ariaLabel = `${subline} - ${headline1} ${headline2}`

        const reducedMotion = useReducedMotion()
        const motion = reducedMotion ? 'reduced' : 'full'
        const recipe = useSlotRecipe({recipe: productAnimatedModuleRecipe})
        const styles = useMemo(() => {
            return recipe({layout, motion})
        }, [layout, recipe, motion])

        const [contentEl, setContentEl] = useState(null)
        const [contentHeight, setContentHeight] = useState(0)

        /*
          - only when the content is taller than the viewport shift the scroll detection with the difference in pixels
          - when the content can fit in the viewport 'start start' is used
        */
        const scrollInitialOffset = useMemo(() => {
            if (!isHydrated) return 'start start'
            return contentHeight > window.innerHeight
                ? `${contentHeight - window.innerHeight}px`
                : 'start start'
        }, [contentHeight, isHydrated])

        const ref = useRef(null)
        const {scrollYProgress} = useScroll({
            target: ref,
            offset: [scrollInitialOffset, 'end end']
        })

        useResizeObserver(contentEl, () => setContentHeight(contentEl.clientHeight))

        return (
            <ModuleContainer
                ref={forwardRef}
                moduleId={moduleId}
                moduleType={moduleType}
                pageId={pageId}
                anchor={anchor}
                background={background}
                css={styles.container}
                aria-label={variant !== 'hidden' ? undefined : ariaLabel}
                spacing="none"
                {...{
                    '--content-height': `${contentHeight}px`
                }}
            >
                <Box {...styles.stickyContainer} ref={ref}>
                    <Box {...styles.stickyBox} ref={setContentEl}>
                        <Box css={styles.textBox}>
                            <Box css={styles.textContent}>
                                <Fade>
                                    <Heading
                                        headingLevel={headingLevel}
                                        {...heading}
                                        alignment="inherit"
                                    />
                                </Fade>
                                {copy?.text && (
                                    <Fade>
                                        <Copy {...copy} alignment="inherit"></Copy>
                                    </Fade>
                                )}
                            </Box>
                        </Box>
                        <Box css={styles.productBox}>
                            <ProductAnimated
                                {...animatedProduct}
                                scrollYProgress={scrollYProgress}
                                containerHeight={containerHeight}
                                layout={layout}
                            />
                        </Box>
                    </Box>
                </Box>
            </ModuleContainer>
        )
    }
)

if (process.env.NODE_ENV !== 'production') {
    ProductAnimatedModule.displayName = 'ProductAnimatedModule'
}

ProductAnimatedModule.propTypes = {
    anchor: PropTypes.string,
    background: PropTypes.oneOf(['default', 'subtle', 'strong', 'inverse']),
    headingLevel: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
    heading: PropTypes.shape({
        headline1: PropTypes.string,
        headline2: PropTypes.string,
        subline: PropTypes.string,
        variant: PropTypes.string,
        alignment: PropTypes.oneOf(['left', 'center'])
    }),
    desktopDisplay: PropTypes.string,
    copy: PropTypes.shape({
        text: PropTypes.string,
        alignment: PropTypes.oneOf(['left', 'center'])
    }),
    animatedProduct: PropTypes.shape({
        imagePrefix: PropTypes.string,
        imageExtension: PropTypes.string,
        imageCount: PropTypes.number,
        imageWidth: PropTypes.number,
        imageHeight: PropTypes.number
    }),
    containerHeight: PropTypes.number,
    moduleId: PropTypes.string,
    moduleType: PropTypes.string,
    pageId: PropTypes.string
}

export default ProductAnimatedModule
